import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string;
  error?: string;
  statusCode?: number;
}

/** Payload inside `data` for GET /dashboard/status */
interface AllModulesStatusDashboardApi {
  totalApplications: number;
  activeApplications: number;
  discussion: number;
  filing: number;
  status?: number;
  registerTms: number;
}

/** Normalized dashboard stats (GET /dashboard/status). */
export interface DashboardStatusSummary {
  totalApplications: number;
  activeApplications: number;
  discussion: number;
  filing: number;
  status: number;
  registeredTms: number;
}

/** One entry in a track result group */
export interface MarkTrackEntry {
  moduleLabel: string;
  module: string;
  mark: string;
  typeOfMark: string;
  classNumber: number;
  markDisplay: string;
  tmNo: string;
  status?: string | null;
  leadId: string;
  applicationId?: string | null;
  acknowledgementNo?: string | null;
  hasAcknowledgement: boolean;
  /** Internal lead reference number. */
  leadNo?: string | null;
  /** Client / applicant name from lead. */
  clientName?: string | null;
  /** Application filing date, yyyy-MM-dd when known. */
  applDate?: string | null;
  /** When status was last updated, ISO-8601 when known. */
  statusUpdatedAt?: string | null;
}

/** One module group in the track result */
export interface MarkTrackGroup {
  moduleLabel: string;
  module: string;
  entries: MarkTrackEntry[];
}

/** Response from GET /dashboard/track */
export interface TrackRecordResult {
  groups: MarkTrackGroup[];
}

/** Single autocomplete suggestion for the Track input */
export interface TrackSuggestion {
  value: string;
  kind: "mark" | "tmNo";
}

/** Response from GET /dashboard/track/suggest */
export interface TrackSuggestResult {
  suggestions: TrackSuggestion[];
}

/**
 * Aggregate stat counts for dashboard cards (GET /dashboard/status).
 */
export const getDashboardStatus = async (): Promise<DashboardStatusSummary> => {
  const res = await apiFetch(`${API_BASE}/dashboard/status`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<AllModulesStatusDashboardApi>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load dashboard stats";
    throw new Error(message);
  }

  const d = (json as ApiSuccessResponse<AllModulesStatusDashboardApi>).data;
  return {
    totalApplications: d.totalApplications,
    activeApplications: d.activeApplications,
    discussion: d.discussion,
    filing: d.filing,
    status: d.status ?? 0,
    registeredTms: d.registerTms,
  };
};

/**
 * Tracks a trademark by mark name (partial match) or TM/Application Number (exact).
 * At least one of mark or tmNo must be provided.
 */
export const trackRecord = async (params: {
  mark?: string;
  tmNo?: string;
}): Promise<TrackRecordResult> => {
  const query = new URLSearchParams();
  if (params.mark?.trim()) query.set("mark", params.mark.trim());
  if (params.tmNo?.trim()) query.set("tmNo", params.tmNo.trim());

  const res = await apiFetch(`${API_BASE}/dashboard/track?${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<TrackRecordResult>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to track record";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<TrackRecordResult>).data;
};

/**
 * Autocomplete suggestions for the Track input. Returns matching mark names and
 * TM/Application numbers (partial, case-insensitive). Empty array on aborted / blank input.
 */
export const suggestTrackRecord = async (
  q: string,
  limit?: number,
  signal?: AbortSignal
): Promise<TrackSuggestResult> => {
  const term = q?.trim() ?? "";
  if (!term) return { suggestions: [] };

  const query = new URLSearchParams();
  query.set("q", term);
  if (limit != null) query.set("limit", String(limit));

  const res = await apiFetch(`${API_BASE}/dashboard/track/suggest?${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal,
  });

  const json = (await res.json()) as
    | ApiSuccessResponse<TrackSuggestResult>
    | ApiErrorResponse;

  if (!res.ok) {
    const message =
      (json as ApiErrorResponse)?.message ||
      (json as ApiErrorResponse)?.error ||
      "Failed to load suggestions";
    throw new Error(message);
  }

  return (json as ApiSuccessResponse<TrackSuggestResult>).data;
};
